import { Link } from '@tanstack/react-router'
import { ShoppingBag } from 'lucide-react'

export type RankingItem = {
  id: string
  name: string
  subtitle?: string | null
  value: number
  href?: string
}

type RankingListProps = {
  items: RankingItem[]
  formatValue: (v: number) => string
  emptyMessage?: string
}

export function RankingList({
  items,
  formatValue,
  emptyMessage = 'Nenhum dado no periodo',
}: RankingListProps) {
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <div className="rounded-full bg-muted p-3 mb-3">
          <ShoppingBag className="h-5 w-5 text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      </div>
    )
  }

  const maxValue = Math.max(...items.map((i) => i.value))

  return (
    <div className="space-y-1">
      {items.map((item, index) => {
        const barWidth = maxValue > 0 ? (item.value / maxValue) * 100 : 0
        const isTop3 = index < 3
        const rowClass =
          'relative flex items-center gap-3 p-2 -mx-2 rounded-lg transition-colors hover:bg-muted/50'
        const inner = (
          <>
            <div
              className="absolute inset-y-0 left-0 rounded-lg bg-primary/8 dark:bg-primary/5 transition-all duration-300"
              style={{ width: `${barWidth}%` }}
            />
            <div
              className={`relative flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold shrink-0 ${
                isTop3 ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
              }`}
            >
              {index + 1}
            </div>
            <div className="relative flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{item.name}</p>
              {item.subtitle && (
                <p className="text-xs text-muted-foreground truncate">{item.subtitle}</p>
              )}
            </div>
            <div className="relative text-right shrink-0">
              <p className="text-sm font-semibold tabular-nums">{formatValue(item.value)}</p>
            </div>
          </>
        )

        if (item.href) {
          return (
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            <Link key={item.id} to={item.href as any} className={rowClass}>
              {inner}
            </Link>
          )
        }

        return (
          <div key={item.id} className={rowClass}>
            {inner}
          </div>
        )
      })}
    </div>
  )
}
